import { legacyApi } from '@/lib/legacy-api.js';

/**
 * Raised when `get_onlinecode` answers with anything but the payload it promised.
 *
 * Same split `PackagesRequestError` and `OrdersRequestError` draw: the vendor's code in the
 * console, a translated line and a retry on screen.
 */
export class CouponsRequestError extends Error {
  constructor(
    readonly endpoint: string,
    readonly code: string,
    message: string,
  ) {
    super(`${endpoint} failed (${code}): ${message}`);
    this.name = 'CouponsRequestError';
  }
}

/**
 * Every coupon ever issued to the account — valid, scheduled, used and long-expired alike.
 *
 * No filtering, server-side or otherwise. The endpoint takes no parameters that narrow it (tried
 * `status` and `limit`; both return the same 78 rows), and the screen has to show all four states
 * anyway. The vendor's own description claims it returns "valid" coupons; every row on the test
 * account is expired, so that is not what it does.
 *
 * `legacyStatusRow` — `{status, row}` — so the client hands back the rows as a bare array.
 */
export async function fetchCouponRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<unknown>('get_onlinecode');

  if (!result.ok) throw new CouponsRequestError('get_onlinecode', result.code, result.message);

  return Array.isArray(result.data) ? result.data : [];
}
